import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Joyride, STATUS, EVENTS } from 'react-joyride';
import { useLocation } from 'react-router-dom';
import WelcomeModal from './WelcomeModal';
import DemoOfferModal from '../demo/DemoOfferModal';
import OnboardingChecklist from './OnboardingChecklist';
import {
  getSportContext,
  fetchSportContext,
  fallbackSportContext,
  refreshFallbackSportContext
} from './ownerOnboarding';
import {
  getTourForRole,
  getChecklistForRole,
  getWelcomeForRole,
  getChecklistUiForRole,
  isOnboardingRole
} from './onboardingRoleConfig';
import { getDemoTourSteps } from '../demo/demoTour';
import { fetchActiveSchoolSportTypeId, fetchOnboardingMy, postOnboarding } from './onboardingApi';
import { fetchDemoStats, markDemoViewed } from '../demo/demoApi';
import { setActiveSchool, isViewingDemoSchool } from '../utils/activeSchool';
import { buildOnboardingTourContext } from './onboardingTourContext';
import {
  prepareNavStepBefore,
  getAttrFromTarget,
  NAV_GROUP_BY_ATTR,
  expandNavGroup
} from './navTourHelpers';
import './onboarding.css';

const OnboardingContext = createContext(null);

const joyrideStyles = {
  overlay: { backgroundColor: 'rgba(0, 0, 0, 0.78)' },
  spotlight: {
    stroke: '#C41E1E',
    strokeWidth: 4,
    fill: 'rgba(57, 73, 171, 0.12)'
  },
  floater: { zIndex: 50001, filter: 'drop-shadow(0 12px 32px rgba(26,35,126,0.28))' },
  tooltip: {
    borderRadius: 12,
    padding: 4,
    maxWidth: 360,
    boxShadow: '0 12px 40px rgba(26,35,126,0.22)'
  },
  tooltipTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: '#0d0d1a',
    marginBottom: 8
  },
  tooltipContent: { fontSize: 14, lineHeight: 1.55, color: '#616161' },
  buttonPrimary: {
    backgroundColor: '#EB3333',
    borderRadius: 8,
    fontSize: 14,
    padding: '10px 20px'
  },
  buttonBack: { color: '#EB3333', fontSize: 14 },
  buttonSkip: { color: '#9e9e9e', fontSize: 13 }
};

function buildJoyrideOptions(locale) {
  return {
    zIndex: 50000,
    primaryColor: '#EB3333',
    textColor: '#212121',
    backgroundColor: '#ffffff',
    overlayColor: 'rgba(0, 0, 0, 0.78)',
    spotlightPadding: 14,
    spotlightRadius: 10,
    offset: 18,
    skipBeacon: true,
    showProgress: true,
    buttons: ['back', 'primary', 'skip'],
    locale
  };
}

/** react-joyride подставляет {current}/{total}, не i18next {{current}}/{{total}} */
function toJoyrideProgressLabel(text) {
  if (typeof text !== 'string') return text;
  return text
    .replace(/\{\{current\}\}/g, '{current}')
    .replace(/\{\{total\}\}/g, '{total}');
}

function enrichTourSteps(steps) {
  return steps.map((step) => {
    const isNav = typeof step.target === 'string' && step.target.includes('nav-');
    const base = {
      ...step,
      skipBeacon: true,
      zIndex: 50000,
      spotlightPadding: 14,
      floatingOptions: {
        shiftOptions: { padding: 20, crossAxis: true },
        flipOptions: { fallbackPlacements: ['right', 'left', 'bottom', 'top'] }
      }
    };
    if (!isNav) return base;
    return {
      ...base,
      placement: 'right',
      before: async () => {
        await prepareNavStepBefore(step.target);
      }
    };
  });
}

export function OnboardingProvider({ children }) {
    const { t, i18n } = useTranslation(['onboarding']);
  const location = useLocation();
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const role = typeof window !== 'undefined' ? localStorage.getItem('role') || '' : '';

  const [sportTypeId, setSportTypeId] = useState(null);
  const [sportContext, setSportContext] = useState(fallbackSportContext);
  const [progress, setProgress] = useState(null);
  const [runTour, setRunTour] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showDemoOffer, setShowDemoOffer] = useState(false);
  const [runDemoTour, setRunDemoTour] = useState(false);
  const [demoTourSteps, setDemoTourSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [localeTick, setLocaleTick] = useState(0);

  const ctx = sportContext;

  useEffect(() => {
    const onLang = () => {
      refreshFallbackSportContext();
      setLocaleTick((n) => n + 1);
    };
    i18n.on('languageChanged', onLang);
    return () => i18n.off('languageChanged', onLang);
  }, [i18n]);

  const welcome = useMemo(() => getWelcomeForRole(role), [role, localeTick]);
  const checklistUi = useMemo(() => getChecklistUiForRole(role), [role, localeTick]);

  const joyrideLocale = useMemo(() => ({
    back: t('onboarding:joyrideBack'),
    close: t('onboarding:joyrideClose'),
    last: t('onboarding:joyrideLast'),
    next: t('onboarding:joyrideNext'),
    nextWithProgress: toJoyrideProgressLabel(t('onboarding:joyrideNextWithProgress')),
    skip: t('onboarding:joyrideSkip')
  }), [t]);

  const joyrideOptions = useMemo(() => buildJoyrideOptions(joyrideLocale), [joyrideLocale]);

  useEffect(() => {
    if (!sportTypeId) {
      setSportContext(getSportContext(null));
      return;
    }
    let cancelled = false;
    fetchSportContext(sportTypeId).then((c) => {
      if (!cancelled) setSportContext(c);
    });
    return () => { cancelled = true; };
  }, [sportTypeId, localeTick]);

  const tourCtx = useMemo(
    () => buildOnboardingTourContext(ctx),
    [ctx, localeTick, location.pathname]
  );

  const tourSteps = useMemo(
    () => enrichTourSteps(getTourForRole(role, tourCtx)),
    [role, tourCtx, localeTick]
  );

  const checklistItems = useMemo(
    () => getChecklistForRole(role, tourCtx),
    [role, tourCtx, localeTick]
  );

  const completedKeys = (progress?.checklistSteps || []).map((s) => s.key || s.Key);

  const load = useCallback(async () => {
    if (!token || role === 'Admin' || !isOnboardingRole(role)) return;
    setLoading(true);
    try {
      const [sportId, prog] = await Promise.all([
        fetchActiveSchoolSportTypeId(),
        fetchOnboardingMy()
      ]);
      setSportTypeId(sportId);
      setProgress(prog);
      const needWelcome = !prog.welcomeSeen;
      const needTour = !prog.tourCompleted && !prog.tourSkippedAt;
      if (needWelcome) {
        setShowWelcome(true);
      } else if (needTour) {
        setTimeout(() => setRunTour(true), 600);
      }
    } catch {
      /* ignore */
    }
    setLoading(false);
  }, [token, role]);

  useEffect(() => {
    load();
  }, [load, location.pathname]);

  async function handleWelcomeStart() {
    setShowWelcome(false);
    await postOnboarding('welcome-seen');
    if (role === 'Owner') {
      setShowDemoOffer(true);
    } else {
      setRunTour(true);
    }
  }

  async function handleDemoSkip() {
    setShowDemoOffer(false);
    setRunTour(true);
  }

  async function handleDemoShow(result) {
    setShowDemoOffer(false);
    setActiveSchool({
      schoolId: result.demoSchoolId,
      schoolName: result.schoolName,
      accessRole: 'demo',
      isDemoSchool: true
    });
    const stats = await fetchDemoStats(result.demoSchoolId).catch(() => ({}));
    setDemoTourSteps(enrichTourSteps(getDemoTourSteps(sportTypeId, stats)));
    if (!progress?.demoViewedAt) {
      setRunDemoTour(true);
      await markDemoViewed();
      setProgress((p) => ({ ...p, demoViewedAt: new Date().toISOString(), demoSchoolId: result.demoSchoolId }));
    }
    window.location.href = '/school';
  }

  async function handleDemoSuccessClose() {
    setShowDemoOffer(false);
    setRunTour(true);
  }

  async function handleWelcomeSkip() {
    setShowWelcome(false);
    await postOnboarding('tour-skip');
    setProgress((p) => ({ ...p, welcomeSeen: true, tourSkippedAt: new Date().toISOString() }));
  }

  async function handleJoyrideEvent(data) {
    if (data.type === EVENTS.STEP_BEFORE && data.step?.target) {
      const attr = getAttrFromTarget(data.step.target);
      const groupId = attr ? NAV_GROUP_BY_ATTR[attr] : null;
      if (groupId) expandNavGroup(groupId);
    }
    if (data.type === EVENTS.TOUR_END) {
      setRunTour(false);
      setRunDemoTour(false);
      if (data.status === STATUS.FINISHED) {
        await postOnboarding('tour-complete');
        setProgress((p) => ({ ...p, tourCompleted: true, welcomeSeen: true }));
      } else if (data.status === STATUS.SKIPPED) {
        await postOnboarding('tour-skip');
        setProgress((p) => ({ ...p, tourSkippedAt: new Date().toISOString(), welcomeSeen: true }));
      }
    }
  }

  useEffect(() => {
    async function onDemoSelected(e) {
      if (role !== 'Owner' || progress?.demoViewedAt) return;
      const school = e.detail;
      if (!school?.schoolId) return;
      const stats = await fetchDemoStats(school.schoolId).catch(() => ({}));
      setDemoTourSteps(enrichTourSteps(getDemoTourSteps(sportTypeId, stats)));
      setRunDemoTour(true);
      await markDemoViewed();
      setProgress((p) => ({ ...p, demoViewedAt: new Date().toISOString(), demoSchoolId: school.schoolId }));
    }
    window.addEventListener('demo-school-selected', onDemoSelected);
    return () => window.removeEventListener('demo-school-selected', onDemoSelected);
  }, [progress?.demoViewedAt, sportTypeId, role]);

  const showChecklist =
    isOnboardingRole(role) &&
    progress &&
    (progress.tourCompleted || progress.tourSkippedAt) &&
    !progress.checklistDismissedAt &&
    !isViewingDemoSchool() &&
    token;

  async function handleDismissChecklist() {
    await postOnboarding('checklist-dismiss');
    setProgress((p) => ({ ...p, checklistDismissedAt: new Date().toISOString() }));
  }

  const showOnboarding = isOnboardingRole(role);

  return (
    <OnboardingContext.Provider value={{ progress, reload: load, sportContext: ctx, sportTypeId }}>
      {children}
      {showOnboarding ? (
        <>
          <WelcomeModal
            visible={showWelcome}
            sportEmoji={ctx.sportEmoji}
            subtitle={welcome.subtitle}
            startLabel={welcome.startLabel}
            onStart={handleWelcomeStart}
            onSkip={handleWelcomeSkip}
          />
          {role === 'Owner' ? (
            <DemoOfferModal
              visible={showDemoOffer}
              sportTypeId={sportTypeId || 1}
              onShowDemo={handleDemoShow}
              onSkip={handleDemoSkip}
              onSuccessClose={handleDemoSuccessClose}
            />
          ) : null}
          <Joyride
            steps={tourSteps}
            run={runTour && !loading && !runDemoTour && tourSteps.length > 0}
            continuous
            scrollToFirstStep
            options={joyrideOptions}
            styles={joyrideStyles}
            locale={joyrideLocale}
            onEvent={handleJoyrideEvent}
          />
          {role === 'Owner' ? (
            <Joyride
              steps={demoTourSteps}
              run={runDemoTour && !loading}
              continuous
              scrollToFirstStep
              options={{ ...joyrideOptions, primaryColor: '#C41E1E' }}
              styles={joyrideStyles}
              locale={{
                ...joyrideLocale,
                last: t('onboarding:joyrideDemoLast')
              }}
              onEvent={handleJoyrideEvent}
            />
          ) : null}
          {showChecklist ? (
            <OnboardingChecklist
              items={checklistItems}
              completedKeys={completedKeys}
              allCompleted={progress.allCompleted}
              onDismiss={handleDismissChecklist}
              title={checklistUi.title}
              doneTitle={checklistUi.doneTitle}
              doneMessage={checklistUi.doneMessage}
              doneButton={checklistUi.doneButton}
              doneRoute={checklistUi.doneRoute}
            />
          ) : null}
        </>
      ) : null}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  return useContext(OnboardingContext);
}

export async function completeOnboardingStep(stepKey) {
  try {
    await postOnboarding('step-complete', { stepKey });
  } catch {
    /* ignore */
  }
}
